import { useEffect, useMemo, useRef } from 'react'
import { LoadingScreen } from './loading-screen'

interface HandoffRedirectProps {
  readonly redirectUrl: string
}

/**
 * F3's own redirect (build plan Track F; docs/decisions.md D-58): a same-origin, auto-submitting
 * GET form rather than `window.location.assign`, so the top-level navigation it performs is
 * `form-action` territory - the one CSP directive `terraform/cloudfront.tf` composes per
 * installation from `general_cognitive_origin` - and is a real, browser-enforced check on the URL,
 * behind the server-side allow-list `redirectUrl` was already validated against when it was stored
 * (`SetExternalHandoffRedirectCommand`).
 *
 * <b>A GET form does NOT carry the action's own query string.</b> An earlier version of this file
 * claimed it did "when the form adds no fields of its own", and shipped: the submission algorithm
 * sets the action URL's query component to the serialized form data set unconditionally, so a form
 * with no fields navigates to the action stripped back to `?`. General Cognitive received neither
 * the session nor the token, and the one test on this area asserted the `action` ATTRIBUTE, which
 * was always correct - the attribute is not where the defect lives.
 *
 * So the query is rebuilt as fields. The action carries origin and path only, and every parameter
 * of the stored URL becomes a hidden input in its original order, which is what the serializer
 * turns back into the identical query string. `URLSearchParams` hands back DECODED values and the
 * serializer re-encodes them, so a base64url token survives the round trip rather than arriving
 * double-encoded.
 */
export function HandoffRedirect({ redirectUrl }: HandoffRedirectProps) {
  const formRef = useRef<HTMLFormElement>(null)

  const { action, fields } = useMemo(() => {
    const parsed = new URL(redirectUrl)
    const carried = [...parsed.searchParams.entries()]

    parsed.search = ''

    return { action: parsed.toString(), fields: carried }
  }, [redirectUrl])

  useEffect(() => {
    formRef.current?.submit()
  }, [action, fields])

  return (
    <>
      <LoadingScreen />
      <form ref={formRef} method="get" action={action} hidden>
        {fields.map(([name, value], index) => (
          <input key={`${name}-${index}`} type="hidden" name={name} value={value} readOnly />
        ))}
      </form>
    </>
  )
}
